import { useCallback, useEffect, useRef, useState } from "react";
import { FamilyStatus } from "protogen/advisors_service_pb";
import { AnimatePresence, motion } from "framer-motion";
import ScrollContainer, {
  ScrollContainerHandle,
} from "components/common/ScrollContainer";
import {
  Avatar,
  Box,
  Button,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowLeft,
  Mail,
  SendHorizontal,
  Smartphone,
  SquarePen,
} from "lucide-react";
import { Attachment } from "protogen/common_pb";
import AttachmentComp from "components/common/AttachmentComp";
import AttachmentDialog from "components/common/AttachmentDialog";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import useTitle from "components/hooks/useTitle";
import useIsMobile from "components/hooks/useIsMobile";
import { useWebSocketContext } from "components/context/SocketConsumerProvider";
import SimpleRichEditor, {
  Handle as SimpleRichEditorHandle,
} from "components/editor/SimpleRichEditor";
import { RichContent } from "components/editor/utils";
import useCached from "components/hooks/useCached";
import { PlainMessage } from "@bufbuild/protobuf";
import {
  AgentConversationThread,
  AgentConversationMessage,
} from "protogen/agent_conversation_service_pb";
import {
  useListAgentConversations,
  useListSmsBotConversations,
  useGetAgentConversation,
  useGetSmsBotConversation,
  useSendAgentMessage,
} from "services/agentConversation";
import { useFetchMemberEmailInbox, useGetMemberEmail } from "services/email";
import { FetchEmailInboxResponse_EmailMessageThread } from "protogen/email_service_pb";
import { EmailMessage } from "protogen/conversation_pb";
import EmailLetter from "components/email/EmailLetter";
import { isFeatureEnabled } from "components/helpers/GatedComponent";
import { PAGE_FRAME_HEIGHT } from "common/constants";
import ActivityFeedEntry from "components/activity/ActivityFeedEntry";
import MessageBubble from "components/activity/MessageBubble";
import ActivityDivider from "components/activity/ActivityDivider";
import DateDisplay from "components/common/DateDisplay";
import { isToday } from "common/utils";
import InlineAgentDisplay from "components/conversation/InlineAgentDisplay";
import LiveInlineAgentDisplay from "components/conversation/LiveInlineAgentDisplay";
import { FunctionCall } from "components/hooks/useTaskAgentState";
import CustomLinkify from "components/common/CustomLinkify";
import DocumentViewer from "components/editor/DocumentViewer";
import { useError } from "components/context/HttpErrorProvider";
import Breadcrumbs from "components/common/Breadcrumbs";
import { useMemberDetails } from "components/context/MemberDetailsContext";

const SIDEBAR_WIDTH = 300;
const SAME_TS_THRESHOLD_SEC = 10 * 60;

type ConversationView = "sms" | "in-app" | "email";

const BOT_AVATAR = (
  <Avatar sx={{ backgroundColor: "#198282", width: 36, height: 36 }}>
    <Smartphone size={18} color="white" />
  </Avatar>
);

const ThreadItem = ({
  thread,
  selected,
  label,
  onClick,
}: {
  thread: PlainMessage<AgentConversationThread>;
  selected: boolean;
  label?: string;
  onClick: () => void;
}) => {
  const ts = thread.lastMessageAtSec
    ? new Date(Number(thread.lastMessageAtSec) * 1000)
    : new Date(Number(thread.createdAtSec) * 1000);

  return (
    <ListItem
      onClick={onClick}
      sx={{
        cursor: "pointer",
        backgroundColor: selected ? "#FAF9FA" : "white",
        gap: "12px",
        padding: "16px 20px",
        "&:hover": { backgroundColor: selected ? "#FAF9FA" : "#FAFAFA" },
        borderBottom: "1px solid #F2F4F7",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: "8px",
          }}
        >
          <Typography
            variant="body"
            fontWeight="500"
            sx={{
              lineHeight: "130%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {label || thread.title || "New Conversation"}
          </Typography>
          {Number(thread.lastMessageAtSec) > 0 && (
            <DateDisplay
              date={ts}
              sx={{
                flexShrink: 0,
                color: "rgba(0,0,0,0.6)",
                textAlign: "right",
              }}
            />
          )}
        </Box>
        {thread.lastMessagePreview && (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {thread.lastMessagePreview}
          </Typography>
        )}
      </Box>
    </ListItem>
  );
};

const EmailThreadItem = ({
  thread,
  selected,
  onClick,
}: {
  thread: PlainMessage<FetchEmailInboxResponse_EmailMessageThread>;
  selected: boolean;
  onClick: () => void;
}) => {
  const ts = thread.lastMessageSec
    ? new Date(Number(thread.lastMessageSec) * 1000)
    : new Date();
  return (
    <ListItem
      onClick={onClick}
      sx={{
        cursor: "pointer",
        backgroundColor: selected ? "#FAF9FA" : "white",
        gap: "12px",
        padding: "16px 20px",
        "&:hover": { backgroundColor: selected ? "#FAF9FA" : "#FAFAFA" },
        borderBottom: "1px solid #F2F4F7",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            gap: "8px",
          }}
        >
          <Typography
            variant="body"
            fontWeight={thread.unread ? "600" : "500"}
            sx={{
              lineHeight: "130%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {thread.subject || "(No subject)"}
          </Typography>
          <DateDisplay
            date={ts}
            sx={{ flexShrink: 0, color: "rgba(0,0,0,0.6)", textAlign: "right" }}
          />
        </Box>
        {thread.teaserText && (
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {thread.teaserText}
          </Typography>
        )}
      </Box>
    </ListItem>
  );
};

const MessageRow = ({
  message,
  prevMessage,
  defaultAgentExpanded,
  capturedAgentCalls,
  onOpenAttachment,
}: {
  message: PlainMessage<AgentConversationMessage>;
  prevMessage?: PlainMessage<AgentConversationMessage>;
  defaultAgentExpanded?: boolean;
  capturedAgentCalls?: Record<string, FunctionCall[]>;
  onOpenAttachment: (a: Attachment) => void;
}) => {
  const isBot = !message.senderRef;
  const entryDt = new Date(Number(message.timestampSec) * 1000);
  const prevDt = prevMessage
    ? new Date(Number(prevMessage.timestampSec) * 1000)
    : null;

  const includeSender =
    !prevMessage || prevMessage.senderRef !== message.senderRef;
  const includeTime =
    !prevMessage ||
    Number(message.timestampSec) - Number(prevMessage.timestampSec) >
      SAME_TS_THRESHOLD_SEC;
  const includeHeader = includeSender || includeTime;

  const showDayDivider =
    prevDt && entryDt.toDateString() !== prevDt.toDateString();
  // With inverse scroll (column-reverse), prevMessage is the older message above.
  // Show "Today" divider when transitioning from non-today to today messages.
  const showTodayDivider =
    isToday(entryDt) && (!prevMessage || !isToday(prevDt!));

  const content = isBot
    ? message.content
    : message.richContent || message.content;

  const isFirstInAgentGroup =
    !prevMessage ||
    prevMessage.agentThreadRef !== message.agentThreadRef ||
    prevMessage.senderRef !== message.senderRef;

  return (
    <>
      <ActivityFeedEntry
        alignLeft={isBot}
        avatarGutter={false}
        headerLeftText={includeHeader ? (isBot ? "Faye" : "You") : null}
        headerRightText={includeHeader ? DateDisplay({ date: entryDt }) : null}
      >
        <>
          {isBot && message.agentThreadRef && isFirstInAgentGroup && (
            <InlineAgentDisplay
              agentThreadData={message.agentThread}
              defaultExpanded={defaultAgentExpanded}
              capturedCalls={capturedAgentCalls?.[message.agentThreadRef]}
            />
          )}
          {content && (
            <MessageBubble alignLeft={isBot}>
              {isBot ? (
                <span style={{ whiteSpace: "pre-wrap" }}>
                  <CustomLinkify
                    handleTel={(e) => `tel:${e}`}
                    handleEmail={(e) => `mailto:${e}`}
                  >
                    {content}
                  </CustomLinkify>
                </span>
              ) : (
                <DocumentViewer
                  key={`${message.ref}`}
                  content={content}
                  attachments={[]}
                  minHeight="0px"
                />
              )}
            </MessageBubble>
          )}
          {message.attachments.length > 0 && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                mt: content ? "8px" : 0,
                alignItems: isBot ? "flex-start" : "flex-end",
              }}
            >
              {message.attachments.map((attachment, i) => (
                <AttachmentComp
                  key={i}
                  attachment={attachment as unknown as Attachment}
                  openAttachment={onOpenAttachment}
                />
              ))}
            </Box>
          )}
        </>
      </ActivityFeedEntry>
      {(showDayDivider || showTodayDivider) && (
        <ActivityDivider
          text={
            isToday(entryDt)
              ? "Today"
              : entryDt.toLocaleDateString(undefined, {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                })
          }
        />
      )}
    </>
  );
};

const TypingRow = () => (
  <ActivityFeedEntry
    alignLeft={true}
    avatarGutter={false}
    headerLeftText="Faye"
    headerRightText={null}
  >
    <MessageBubble alignLeft={true} loading={true}>
      {""}
    </MessageBubble>
  </ActivityFeedEntry>
);

const ConversationPanel = ({
  thread,
  title,
  avatar,
  subtitle,
  messages,
  botThinking,
  liveAgentThreadRef,
  onAgentComplete,
  capturedAgentCalls,
  readOnly,
  cacheKey,
  onSend,
  sendLoading,
  scrollRef,
  hasMore,
  fetchMore,
  onBack,
  initialContent,
  autoFocus,
  onOpenAttachment,
}: {
  thread?: PlainMessage<AgentConversationThread>;
  title?: string;
  avatar?: React.ReactNode;
  subtitle?: string;
  messages: PlainMessage<AgentConversationMessage>[];
  botThinking: boolean;
  liveAgentThreadRef?: string;
  onAgentComplete?: (calls: FunctionCall[]) => void;
  capturedAgentCalls?: Record<string, FunctionCall[]>;
  readOnly?: boolean;
  cacheKey: string;
  onSend: (content: RichContent) => void;
  sendLoading: boolean;
  scrollRef: React.RefObject<ScrollContainerHandle>;
  hasMore: boolean;
  fetchMore: () => void;
  onBack?: () => void;
  initialContent?: string;
  autoFocus?: boolean;
  onOpenAttachment: (a: Attachment) => void;
}) => {
  const editorRef = useRef<SimpleRichEditorHandle>(null);

  useEffect(() => {
    if (autoFocus) {
      // Delay slightly to let the editor mount.
      const timer = setTimeout(() => editorRef.current?.focus(), 50);
      return () => clearTimeout(timer);
    }
  }, [autoFocus]);

  const [content, setContent] = useCached<RichContent | null>(
    cacheKey,
    initialContent
      ? {
          text: initialContent,
          html: initialContent,
          json: "",
          attachments: [],
        }
      : null,
  );
  const [resetEditor, setResetEditor] = useState(false);

  // When prefill is provided, override any cached draft.
  useEffect(() => {
    if (initialContent) {
      setContent({
        text: initialContent,
        html: initialContent,
        json: "",
        attachments: [],
      });
    }
  }, [initialContent]);

  const canSend =
    !sendLoading &&
    (!!content?.text?.trim() || (content?.attachments?.length ?? 0) > 0);

  const handleSend = () => {
    if (!canSend || !content) return;
    onSend(content);
    setResetEditor((v) => !v);
    setContent(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "14px 16px",
          borderBottom: "1px solid #EAEBEC",
          backgroundColor: "#FFFFFF",
        }}
      >
        {onBack && (
          <IconButton size="small" onClick={onBack} sx={{ color: "#6B7280" }}>
            <ArrowLeft size={20} />
          </IconButton>
        )}
        {avatar ?? BOT_AVATAR}
        <Box>
          <Typography
            sx={{ fontSize: "15px", fontWeight: 600, color: "#101828" }}
          >
            {title || thread?.title || "New Conversation"}
          </Typography>
          <Typography variant="bodySmall" color="text.secondary">
            {subtitle ?? "Faye AI"}
          </Typography>
        </Box>
      </Box>

      <ScrollContainer
        ref={scrollRef}
        fetchMoreData={fetchMore}
        hasMore={hasMore}
        sx={{
          flex: "1 1 0",
          padding: "8px 16px",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        {botThinking && <TypingRow />}
        {liveAgentThreadRef && (
          <LiveInlineAgentDisplay
            threadRef={liveAgentThreadRef}
            onComplete={onAgentComplete}
          />
        )}
        {[...messages].reverse().map((msg, i, arr) => {
          const isLastBotAgent =
            !msg.senderRef &&
            !!msg.agentThreadRef &&
            !arr.slice(0, i).some((m) => !m.senderRef && m.agentThreadRef);
          return (
            <MessageRow
              key={msg.ref}
              message={msg}
              prevMessage={arr[i + 1]}
              defaultAgentExpanded={isLastBotAgent}
              capturedAgentCalls={capturedAgentCalls}
              onOpenAttachment={onOpenAttachment}
            />
          );
        })}
      </ScrollContainer>

      {readOnly ? (
        <Box
          sx={{
            borderTop: "1px solid #EAEBEC",
            padding: "12px 16px",
            backgroundColor: "#FAFAFA",
            textAlign: "center",
          }}
        >
          <Typography variant="bodySmall" color="text.secondary">
            Replies continue via text message
          </Typography>
        </Box>
      ) : (
        <Box
          sx={{
            borderTop: "1px solid #EAEBEC",
            padding: "12px 16px",
            backgroundColor: "#FFFFFF",
          }}
        >
          <SimpleRichEditor
            ref={editorRef}
            setContent={setContent}
            placeholder="Message Faye AI..."
            triggerReset={resetEditor}
            onReset={() => setResetEditor(false)}
            initialContent={initialContent || content?.html}
            allowAttachments={true}
            editorMaxHeight="240px"
            imgMaxWidth="160px"
            scrollable
            onKeyDown={(_view, event) => {
              if (event.key === "Enter" && !event.shiftKey) {
                handleSend();
                return true;
              }
              return false;
            }}
            primaryAction={
              <IconButton
                disabled={!canSend}
                onClick={handleSend}
                sx={{ color: canSend ? "#198282" : undefined, padding: "4px" }}
              >
                {sendLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <SendHorizontal size={20} />
                )}
              </IconButton>
            }
          />
        </Box>
      )}
    </>
  );
};

const NoConversations = () => (
  <Box sx={{ padding: "24px 16px", textAlign: "center" }}>
    <Typography variant="bodySmall" color="text.secondary">
      No conversations
    </Typography>
  </Box>
);

const EmptyState = ({ onCreate }: { onCreate: () => void }) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      flex: 1,
      gap: "16px",
      backgroundColor: "#FAFAFA",
      height: "100%",
    }}
  >
    <Avatar sx={{ backgroundColor: "#198282", width: 56, height: 56 }}>
      <Smartphone size={28} color="white" />
    </Avatar>
    <Typography variant="h4" sx={{ color: "#374151" }}>
      Conversations
    </Typography>
    <Typography
      sx={{
        fontSize: "14px",
        textAlign: "center",
        maxWidth: "300px",
        color: "#6B7280",
      }}
    >
      Ask Faye anything — your personal AI assistant is ready to help.
    </Typography>
    <Button
      variant="contained"
      onClick={onCreate}
      startIcon={<SquarePen size={16} />}
    >
      Start a conversation
    </Button>
  </Box>
);

const combineMessages = (
  prev: PlainMessage<AgentConversationMessage>[],
  next: PlainMessage<AgentConversationMessage>[],
) => {
  const nextByRef = new Map(next.map((m) => [m.ref, m]));
  // Update existing messages in place (e.g. agentThread data arriving after socket add)
  const merged = prev.map((m) => {
    const update = nextByRef.get(m.ref);
    return update ? { ...m, ...update } : m;
  });
  const seen = new Set(prev.map((m) => m.ref));
  return [...merged, ...next.filter((m) => !seen.has(m.ref))];
};

const hasBotReplyAfter = (
  messages: PlainMessage<AgentConversationMessage>[],
  sentAtSec: bigint,
) =>
  messages.some(
    (message) =>
      !message.senderRef && BigInt(message.timestampSec ?? 0) >= sentAtSec,
  );

export default () => {
  useTitle("Conversations");
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const params = useParams<{ view?: string; ref?: string }>();
  const location = useLocation();
  const { registerHandler, unregisterHandler, isConnected } =
    useWebSocketContext();
  const prefillText = (location.state as { prefill?: string } | null)?.prefill;

  const view: ConversationView =
    params.view === "sms"
      ? "sms"
      : params.view === "email"
        ? "email"
        : "in-app";
  const selectedRef = params.ref ? decodeURIComponent(params.ref) : null;

  const [threads, setThreads] = useState<
    PlainMessage<AgentConversationThread>[]
  >([]);
  const [smsThreads, setSmsThreads] = useState<
    PlainMessage<AgentConversationThread>[]
  >([]);
  const [smsMessages, setSmsMessages] = useState<
    PlainMessage<AgentConversationMessage>[]
  >([]);
  const [smsBotCursor, setSmsBotCursor] = useState("");
  const [smsBotHasMore, setSmsBotHasMore] = useState(false);
  const [isComposing, setIsComposing] = useState(false);
  const [messages, setMessages] = useState<
    PlainMessage<AgentConversationMessage>[]
  >([]);
  const [messagesCursor, setMessagesCursor] = useState("");
  const [messagesHasMore, setMessagesHasMore] = useState(false);
  const [pendingBotReply, setPendingBotReply] = useState<{
    conversationRef: string;
    sentAtSec: bigint;
  } | null>(null);
  const [pendingAgentThreadRef, setPendingAgentThreadRef] = useState<
    string | null
  >(null);

  const [emailThreads, setEmailThreads] = useState<
    PlainMessage<FetchEmailInboxResponse_EmailMessageThread>[]
  >([]);
  const [emailDetail, setEmailDetail] = useState<EmailMessage | null>(null);
  const [emailListLoading, setEmailListLoading] = useState(false);

  const [openAttachment, setOpenAttachment] = useState<Attachment | null>(null);
  const [capturedAgentCalls, setCapturedAgentCalls] = useState<
    Record<string, FunctionCall[]>
  >({});
  const lastCompletedAgentThreadRefRef = useRef<string | null>(null);
  const pendingAgentThreadRefStable = useRef<string | null>(null);

  const selectedRefStable = useRef<string | null>(null);
  const viewStable = useRef<ConversationView>(view);
  const smsBotScrollRef = useRef<ScrollContainerHandle>(null);
  const inAppScrollRef = useRef<ScrollContainerHandle>(null);
  const smsBotHasLoadedOnce = useRef(false);
  const messagesHasLoadedOnce = useRef(false);
  const smsBotLoadingMore = useRef(false);
  const messagesLoadingMore = useRef(false);
  const lastSmsBotRef = useRef("");
  const lastMessagesRef = useRef("");

  const { request: listConversations, loading: listLoading } =
    useListAgentConversations();
  const { request: listSmsBotConversations } = useListSmsBotConversations();
  const { request: getConversation } = useGetAgentConversation();
  const { request: getSmsBotConversation } = useGetSmsBotConversation();
  const { request: sendMessage, loading: sendLoading } = useSendAgentMessage();
  const { addError } = useError();
  const { request: fetchEmailInbox } = useFetchMemberEmailInbox();
  const { request: getMemberEmail } = useGetMemberEmail();
  const { isDigitalSubscription, familyStatus } = useMemberDetails();
  const isDeactivated =
    familyStatus === FamilyStatus.DEACTIVATED ||
    familyStatus === FamilyStatus.DIGITAL_DEACTIVATED ||
    familyStatus === FamilyStatus.DIGITAL_DEACTIVATED_TRIAL ||
    familyStatus === FamilyStatus.DEACTIVATED_PROSPECT;
  const showEmailTab =
    isDigitalSubscription || isFeatureEnabled("faye.member-email-inbox", true);

  const loadThreads = useCallback(async () => {
    const resp = await listConversations({});
    if (resp) setThreads(resp.threads);
  }, []);

  const loadSmsThreads = useCallback(async () => {
    const resp = await listSmsBotConversations({});
    if (resp) setSmsThreads(resp.threads);
  }, []);

  const loadSmsMessages = useCallback(async (conversationRef: string) => {
    const resp = await getSmsBotConversation({
      conversationRef,
      startCursor: "",
      pageSize: 50,
    });
    if (!resp) return;
    setSmsMessages((prev) => combineMessages(prev, resp.messages));
    if (!smsBotHasLoadedOnce.current) {
      smsBotHasLoadedOnce.current = true;
      setSmsBotCursor(resp.nextCursor);
      setSmsBotHasMore(resp.hasMore);
    }
    if (resp.thread) {
      setSmsThreads((prev) =>
        prev.map((t) => (t.ref === conversationRef ? resp.thread! : t)),
      );
    }
  }, []);

  const fetchMoreSmsBotMessages = useCallback(async (cursor: string) => {
    if (smsBotLoadingMore.current || !cursor) return;
    smsBotLoadingMore.current = true;
    const resp = await getSmsBotConversation({
      conversationRef: selectedRefStable.current!,
      startCursor: cursor,
      pageSize: 50,
    });
    smsBotLoadingMore.current = false;
    if (!resp) return;
    setSmsMessages((prev) => [...resp.messages, ...prev]);
    setSmsBotCursor(resp.nextCursor);
    setSmsBotHasMore(resp.hasMore);
  }, []);

  const loadMessages = useCallback(async (conversationRef: string) => {
    const resp = await getConversation({
      conversationRef,
      startCursor: "",
      pageSize: 50,
    });
    if (!resp) return;
    setMessages((prev) => combineMessages(prev, resp.messages));
    if (!messagesHasLoadedOnce.current) {
      messagesHasLoadedOnce.current = true;
      setMessagesCursor(resp.nextCursor);
      setMessagesHasMore(resp.hasMore);
    }
    if (resp.thread) {
      setThreads((prev) =>
        prev.map((t) => (t.ref === conversationRef ? resp.thread! : t)),
      );
    }
    setPendingBotReply((pending) => {
      if (!pending || pending.conversationRef !== conversationRef) {
        return pending;
      }
      return hasBotReplyAfter(resp.messages, pending.sentAtSec)
        ? null
        : pending;
    });
  }, []);

  const loadEmailInbox = useCallback(async () => {
    setEmailListLoading(true);
    const resp = await fetchEmailInbox({ cursor: "" });
    if (resp) setEmailThreads(resp.threads);
    setEmailListLoading(false);
  }, []);

  const loadEmailDetail = useCallback(async (emailRef: string) => {
    setEmailDetail(null);
    const resp = await getMemberEmail({ emailRef });
    if (resp?.email) setEmailDetail(resp.email);
  }, []);

  const fetchMoreMessages = useCallback(async (cursor: string) => {
    if (messagesLoadingMore.current || !cursor) return;
    messagesLoadingMore.current = true;
    const resp = await getConversation({
      conversationRef: selectedRefStable.current!,
      startCursor: cursor,
      pageSize: 50,
    });
    messagesLoadingMore.current = false;
    if (!resp) return;
    setMessages((prev) => [...resp.messages, ...prev]);
    setMessagesCursor(resp.nextCursor);
    setMessagesHasMore(resp.hasMore);
  }, []);

  useEffect(() => {
    if (view === "in-app") {
      loadThreads();
    } else if (view === "sms") {
      loadSmsThreads();
    }
  }, [view]);

  useEffect(() => {
    const latest = smsMessages[smsMessages.length - 1]?.ref;
    if (latest && latest !== lastSmsBotRef.current) {
      lastSmsBotRef.current = latest;
      smsBotScrollRef.current?.setSticky();
    }
  }, [smsMessages]);

  useEffect(() => {
    const latest = messages[messages.length - 1]?.ref;
    if (latest && latest !== lastMessagesRef.current) {
      lastMessagesRef.current = latest;
      inAppScrollRef.current?.setSticky();
    }
  }, [messages]);

  useEffect(() => {
    if (
      view === "in-app" &&
      selectedRef &&
      pendingBotReply?.conversationRef === selectedRef
    ) {
      inAppScrollRef.current?.setSticky();
    }
  }, [pendingBotReply, selectedRef, view]);

  useEffect(() => {
    selectedRefStable.current = selectedRef;
  }, [selectedRef]);

  useEffect(() => {
    viewStable.current = view;
  }, [view]);

  useEffect(() => {
    pendingAgentThreadRefStable.current = pendingAgentThreadRef;
  }, [pendingAgentThreadRef]);

  // Poll for bot reply when socket is disconnected.
  useEffect(() => {
    if (!pendingBotReply || isConnected) return;
    const interval = setInterval(() => {
      loadMessages(pendingBotReply.conversationRef);
    }, 30000);
    return () => clearInterval(interval);
  }, [pendingBotReply, isConnected, loadMessages]);

  // On reconnect, immediately catch up rather than waiting for the next poll cycle.
  // The poll above clears its interval when isConnected goes true, but any reply that
  // arrived during the disconnection window would be permanently missed without this.
  const prevIsConnectedRef = useRef(isConnected);
  useEffect(() => {
    const wasConnected = prevIsConnectedRef.current;
    prevIsConnectedRef.current = isConnected;
    if (pendingBotReply && isConnected && !wasConnected) {
      loadMessages(pendingBotReply.conversationRef);
    }
  }, [isConnected, pendingBotReply, loadMessages]);

  // Synchronously track agent thread completion so we can tag the incoming
  // bot text message with the right agentThreadRef before React effects fire.
  useEffect(() => {
    const handler = (channel: string, rawData: unknown) => {
      const pending = pendingAgentThreadRefStable.current;
      if (!pending) return;
      if (channel !== `task-agent/${encodeURIComponent(pending)}`) return;
      const data = rawData as { eventType?: string };
      if (
        data.eventType === "thread_completed" ||
        data.eventType === "thread_failed"
      ) {
        lastCompletedAgentThreadRefRef.current = pending;
      }
    };
    registerHandler("task-agent/", handler);
    return () => unregisterHandler("task-agent/", handler);
  }, [registerHandler, unregisterHandler]);

  useEffect(() => {
    if (prefillText && !selectedRef) {
      setIsComposing(true);
      setMessages([]);
      setPendingBotReply(null);
    }
  }, [prefillText]);

  useEffect(() => {
    if (selectedRef && view === "in-app") {
      const isPendingFirstMessageTransition =
        pendingBotReply?.conversationRef === selectedRef;
      if (!isPendingFirstMessageTransition) {
        setMessages([]);
        setPendingAgentThreadRef(null);
        setCapturedAgentCalls({});
        lastCompletedAgentThreadRefRef.current = null;
      }
      setPendingBotReply((pending) =>
        pending && pending.conversationRef === selectedRef ? pending : null,
      );
      setMessagesCursor("");
      setMessagesHasMore(false);
      messagesHasLoadedOnce.current = false;
      messagesLoadingMore.current = false;
      lastMessagesRef.current = "";
      loadMessages(selectedRef);
    } else if (selectedRef && view === "sms") {
      setSmsMessages([]);
      loadSmsMessages(selectedRef);
    }
  }, [selectedRef, view]);

  useEffect(() => {
    if (view === "email") {
      loadEmailInbox();
    }
  }, [view]);

  useEffect(() => {
    if (view === "email" && selectedRef) {
      loadEmailDetail(selectedRef);
    } else if (view !== "email") {
      setEmailDetail(null);
    }
  }, [view, selectedRef]);

  useEffect(() => {
    const debounceTimer = { current: null as NodeJS.Timeout | null };
    const handler = (_channel: string, rawData: unknown) => {
      const data = rawData as {
        conversationRef?: string;
        textMessageRef?: string;
        content?: string;
        richContent?: string;
        senderRef?: string;
        timestampSec?: number;
        attachments?: {
          filename?: string;
          contentType?: string;
          url?: string;
          fileSize?: number;
        }[];
      };
      const ref = selectedRefStable.current;
      const currentView = viewStable.current;

      // notifier sends URL-encoded refs (to_url_encoded_id); decode before comparing.
      const conversationRef = data.conversationRef
        ? decodeURIComponent(data.conversationRef)
        : "";
      const textMessageRef = data.textMessageRef
        ? decodeURIComponent(data.textMessageRef)
        : "";
      const senderRef = data.senderRef
        ? decodeURIComponent(data.senderRef)
        : "";

      if (
        currentView === "in-app" &&
        ref &&
        conversationRef === ref &&
        textMessageRef
      ) {
        const isBot = !senderRef;
        const incoming: PlainMessage<AgentConversationMessage> = {
          ref: textMessageRef,
          content: data.content ?? "",
          richContent: data.richContent ?? "",
          senderRef: senderRef,
          timestampSec: BigInt(data.timestampSec ?? 0),
          attachments: (data.attachments || []).map((a) => ({
            filename: a.filename ?? "",
            contentType: a.contentType ?? "",
            url: a.url ?? "",
            fileSize: a.fileSize ?? 0,
            inlineReference: "",
          })),
          agentThreadRef: isBot
            ? lastCompletedAgentThreadRefRef.current ?? ""
            : "",
          agentThread: undefined,
        };
        setMessages((prev) => combineMessages(prev, [incoming]));
        setPendingBotReply((pending) => {
          if (!pending || pending.conversationRef !== ref) return pending;
          return BigInt(data.timestampSec ?? 0) >= pending.sentAtSec
            ? null
            : pending;
        });
      }

      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      debounceTimer.current = setTimeout(() => {
        loadThreads();
        loadSmsThreads();
        const latestRef = selectedRefStable.current;
        const latestView = viewStable.current;
        if (latestRef && latestView === "sms") {
          loadSmsMessages(latestRef);
        }
        // In-app messages arrive via socket; agent output from capturedAgentCalls.
      }, 1000);
    };
    registerHandler("textmessage/", handler);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      unregisterHandler("textmessage/", handler);
    };
  }, [
    registerHandler,
    unregisterHandler,
    loadThreads,
    loadSmsThreads,
    loadSmsMessages,
  ]);

  const handleSelectThread = (ref: string) => {
    setIsComposing(false);
    navigate(`/conversations/in-app/${encodeURIComponent(ref)}`);
  };

  const handleSelectSmsThread = (ref: string) => {
    navigate(`/conversations/sms/${encodeURIComponent(ref)}`);
  };

  const handleNewConversation = () => {
    setIsComposing(true);
    setMessages([]);
    setPendingBotReply(null);
    navigate("/conversations/in-app");
  };

  const handleSend = async (richContent: RichContent) => {
    if (!richContent.text?.trim() && !richContent.attachments?.length) return;
    let resp: Awaited<ReturnType<typeof sendMessage>> = null;
    try {
      resp = await sendMessage({
        conversationRef: selectedRef ?? "",
        content: richContent.text,
        richContent: richContent.html,
        attachments: richContent.attachments,
      });
    } catch (error) {
      if (error instanceof Error) {
        addError(error.message || "Unable to send message.");
      } else {
        addError("Unable to send message.");
      }
      return;
    }
    if (!resp?.message) {
      return;
    }
    const response = resp;
    if (response.thread) {
      setThreads((prev) => [response.thread!, ...prev]);
      setIsComposing(false);
      localStorage.removeItem("web-bot-compose:new");
      navigate(
        `/conversations/in-app/${encodeURIComponent(response.thread.ref)}`,
      );
    }
    const sentMessage = response.message;
    if (!sentMessage) {
      return;
    }
    setMessages((prev) => [...prev, sentMessage]);
    const pendingConversationRef = response.thread?.ref || selectedRef;
    if (pendingConversationRef) {
      setPendingBotReply({
        conversationRef: pendingConversationRef,
        sentAtSec: BigInt(sentMessage.timestampSec ?? 0),
      });
    }
    if (response.agentThreadRef) {
      setPendingAgentThreadRef(response.agentThreadRef);
    }
  };

  const handleViewChange = (newView: ConversationView) => {
    navigate(`/conversations/${newView}`);
  };

  const handleSelectEmail = (emailRef: string) => {
    navigate(`/conversations/email/${encodeURIComponent(emailRef)}`);
  };

  const pageHeight = isMobile ? "calc(100vh - 51px)" : PAGE_FRAME_HEIGHT;
  const botThinking =
    !!selectedRef &&
    view === "in-app" &&
    pendingBotReply?.conversationRef === selectedRef;
  const showInAppConversation = selectedRef !== null || isComposing;
  const selectedThread = threads.find((t) => t.ref === selectedRef);
  const selectedSmsThread = smsThreads.find((t) => t.ref === selectedRef);
  const mobileShowDetail =
    (view === "sms" && selectedRef !== null) ||
    showInAppConversation ||
    (view === "email" && selectedRef !== null);
  const inAppCacheKey = isComposing
    ? "web-bot-compose:new"
    : `web-bot-compose:${selectedRef}`;

  const tabs = [
    { value: "sms" as ConversationView, label: "Text" },
    { value: "in-app" as ConversationView, label: "In-app" },
    ...(showEmailTab
      ? [{ value: "email" as ConversationView, label: "Email" }]
      : []),
  ];

  const conversationsBreadcrumbs = isDigitalSubscription ? (
    <Box sx={{ paddingBottom: "8px" }}>
      <Breadcrumbs
        breadcrumbs={[
          { name: "Home", link: "/home" },
          { name: "Conversations" },
        ]}
      />
    </Box>
  ) : null;

  const filterButtons = (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        height: "36px",
        backgroundColor: "#F6F6F6",
        borderRadius: "100px",
        padding: "2px",
      }}
    >
      {tabs.map((tab) => (
        <Box
          key={tab.value}
          onClick={() => handleViewChange(tab.value)}
          sx={{
            flex: 1,
            position: "relative",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            borderRadius: "100px",
          }}
        >
          {view === tab.value && (
            <motion.div
              layoutId="tab-indicator"
              style={{
                position: "absolute",
                inset: 0,
                backgroundColor: "#FFF",
                borderRadius: "100px",
                boxShadow: "0px 1px 6px 0px rgba(0, 0, 0, 0.15)",
              }}
              transition={{ type: "spring", bounce: 0.15, duration: 0.35 }}
            />
          )}
          <Typography
            variant="bodySmall"
            sx={{
              position: "relative",
              zIndex: 1,
              fontWeight: 500,
              color: "#3D3D3D",
              userSelect: "none",
            }}
          >
            {tab.label.replace("-", " ")}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const smsConversationPanel = (
    <ConversationPanel
      title={selectedSmsThread?.title || "Text messages"}
      avatar={BOT_AVATAR}
      subtitle="Replies continue via text message"
      messages={smsMessages}
      botThinking={false}
      readOnly={true}
      cacheKey={`web-bot-compose:sms:${selectedRef}`}
      sendLoading={false}
      onSend={() => {}}
      scrollRef={smsBotScrollRef}
      hasMore={smsBotHasMore}
      fetchMore={() => fetchMoreSmsBotMessages(smsBotCursor)}
      onBack={isMobile ? () => navigate("/conversations/sms") : undefined}
      onOpenAttachment={setOpenAttachment}
    />
  );

  const activeAgentThreadRef =
    selectedRef && view === "in-app" ? pendingAgentThreadRef : null;

  const handleAgentComplete = useCallback((calls: FunctionCall[]) => {
    const ref = pendingAgentThreadRefStable.current;
    if (ref) {
      setCapturedAgentCalls((prev) => ({ ...prev, [ref]: calls }));
    }
    setPendingAgentThreadRef(null);
  }, []);

  const inAppConversationPanel = (
    <ConversationPanel
      key={inAppCacheKey}
      thread={selectedThread}
      messages={selectedRef ? messages : []}
      botThinking={selectedRef ? botThinking : false}
      liveAgentThreadRef={activeAgentThreadRef ?? undefined}
      onAgentComplete={handleAgentComplete}
      capturedAgentCalls={capturedAgentCalls}
      cacheKey={inAppCacheKey}
      sendLoading={sendLoading}
      onSend={handleSend}
      scrollRef={inAppScrollRef}
      hasMore={messagesHasMore}
      fetchMore={() => fetchMoreMessages(messagesCursor)}
      onBack={
        isMobile
          ? () => {
              setIsComposing(false);
              navigate("/conversations/in-app");
            }
          : undefined
      }
      autoFocus={isComposing || !!selectedRef}
      initialContent={isComposing ? prefillText : undefined}
      onOpenAttachment={setOpenAttachment}
    />
  );

  const emailDetailPanel = emailDetail ? (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "14px 16px",
          borderBottom: "1px solid #EAEBEC",
          backgroundColor: "#FFFFFF",
        }}
      >
        {isMobile && (
          <IconButton
            size="small"
            onClick={() => navigate("/conversations/email")}
            sx={{ color: "#6B7280" }}
          >
            <ArrowLeft size={20} />
          </IconButton>
        )}
        <Avatar sx={{ backgroundColor: "#198282", width: 36, height: 36 }}>
          <Mail size={18} color="white" />
        </Avatar>
        <Box sx={{ flex: 1, overflow: "hidden" }}>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: "#101828",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {emailDetail.subject || "(No subject)"}
          </Typography>
          <Typography variant="bodySmall" color="text.secondary">
            {emailDetail.senderDisplayName}
          </Typography>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflowY: "auto", padding: "16px" }}>
        <EmailLetter email={emailDetail} />
      </Box>
    </Box>
  ) : (
    <Box
      sx={{
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#FAFAFA",
      }}
    >
      {emailListLoading ? (
        <CircularProgress size={24} />
      ) : (
        <Box sx={{ maxWidth: "320px", textAlign: "center", px: "16px" }}>
          <Typography variant="bodySmall" color="text.secondary">
            Select an email to view
          </Typography>
          <Typography
            variant="bodySmall"
            color="text.secondary"
            sx={{ mt: "8px" }}
          >
            Send an email to inbox@findfaye.com to see it here and let your
            agent help with it.
          </Typography>
        </Box>
      )}
    </Box>
  );

  const emailLeftPanel = (
    <List disablePadding sx={{ flex: 1, overflowY: "auto" }}>
      {emailListLoading && emailThreads.length === 0 && (
        <Box
          sx={{ display: "flex", justifyContent: "center", padding: "32px" }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      {emailThreads.map((thread) => (
        <EmailThreadItem
          key={thread.latestEmailRef}
          thread={thread}
          selected={thread.latestEmailRef === selectedRef}
          onClick={() => handleSelectEmail(thread.latestEmailRef)}
        />
      ))}
    </List>
  );

  // Mobile: single-panel navigation.
  if (isMobile) {
    // Conversation detail view
    if (mobileShowDetail) {
      return (
        <Box
          className="two-panel-grid-container"
          sx={{
            display: "flex",
            flexDirection: "column",
            height: pageHeight,
            overflow: "hidden",
            backgroundColor: "#FFFFFF",
          }}
        >
          {conversationsBreadcrumbs && (
            <Box
              sx={{
                flexShrink: 0,
                padding: "12px 16px 0",
                borderBottom: "1px solid #EAEBEC",
                backgroundColor: "#FFFFFF",
              }}
            >
              {conversationsBreadcrumbs}
            </Box>
          )}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {view === "sms"
              ? smsConversationPanel
              : view === "email"
                ? emailDetailPanel
                : inAppConversationPanel}
          </Box>
          <AttachmentDialog
            attachment={openAttachment}
            onClose={() => setOpenAttachment(null)}
          />
        </Box>
      );
    }

    // List view (always shows filter + list)
    return (
      <Box
        className="two-panel-grid-container"
        sx={{
          display: "flex",
          flexDirection: "column",
          height: pageHeight,
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "16px 16px 12px",
            borderBottom: "1px solid #EAEBEC",
          }}
        >
          {conversationsBreadcrumbs}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "40px",
            }}
          >
            <Typography variant="title">Conversations</Typography>
            <AnimatePresence>
              {view === "in-app" && !isDeactivated && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Tooltip title="New conversation">
                    <IconButton
                      onClick={handleNewConversation}
                      sx={{
                        display: "flex",
                        padding: "8px",
                        alignItems: "flex-start",
                        gap: "1px",
                        borderRadius: "100px",
                        border: "2px solid #EAEBEC",
                        background: "#FFF",
                      }}
                    >
                      <SquarePen height={20} width={20} stroke="#198282" />
                    </IconButton>
                  </Tooltip>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          {filterButtons}
        </Box>
        {view === "email" ? (
          emailLeftPanel
        ) : view === "sms" ? (
          <List disablePadding sx={{ flex: 1, overflowY: "auto" }}>
            {smsThreads.length === 0 && <NoConversations />}
            {smsThreads.map((thread) => (
              <ThreadItem
                key={thread.ref}
                thread={thread}
                selected={thread.ref === selectedRef}
                onClick={() => handleSelectSmsThread(thread.ref)}
              />
            ))}
          </List>
        ) : (
          <List disablePadding sx={{ flex: 1, overflowY: "auto" }}>
            {listLoading && threads.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "32px",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            {threads.length === 0 && !listLoading && <NoConversations />}
            {threads.map((thread) => (
              <ThreadItem
                key={thread.ref}
                thread={thread}
                selected={thread.ref === selectedRef}
                onClick={() => handleSelectThread(thread.ref)}
              />
            ))}
          </List>
        )}
        <AttachmentDialog
          attachment={openAttachment}
          onClose={() => setOpenAttachment(null)}
        />
      </Box>
    );
  }

  // Desktop: two-panel layout.
  return (
    <Box
      className="two-panel-grid-container"
      sx={{
        display: "flex",
        height: pageHeight,
        overflow: "hidden",
        background: "#FDFAF7",
      }}
    >
      {/* Left panel */}
      <Box
        sx={{
          width: SIDEBAR_WIDTH,
          flexShrink: 0,
          display: "flex",
          flexDirection: "column",
          borderRadius: "16px 0 0 0",
          border: "1px solid #EAECF0",
          background: "white",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            padding: "16px 16px 12px",
            borderBottom: "1px solid #EAEBEC",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              height: "40px",
            }}
          >
            <Typography variant="title">Conversations</Typography>
            <AnimatePresence>
              {view === "in-app" && !isDeactivated && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.15 }}
                >
                  <Tooltip title="New conversation">
                    <IconButton
                      onClick={handleNewConversation}
                      sx={{
                        display: "flex",
                        padding: "8px",
                        alignItems: "flex-start",
                        gap: "1px",
                        borderRadius: "100px",
                        border: "2px solid #EAEBEC",
                        background: "#FFF",
                      }}
                    >
                      <SquarePen height={20} width={20} stroke="#198282" />
                    </IconButton>
                  </Tooltip>
                </motion.div>
              )}
            </AnimatePresence>
          </Box>
          {filterButtons}
        </Box>

        {view === "sms" ? (
          <List disablePadding sx={{ flex: 1, overflowY: "auto" }}>
            {smsThreads.length === 0 && <NoConversations />}
            {smsThreads.map((thread) => (
              <ThreadItem
                key={thread.ref}
                thread={thread}
                selected={thread.ref === selectedRef}
                onClick={() => handleSelectSmsThread(thread.ref)}
              />
            ))}
          </List>
        ) : view === "email" ? (
          emailLeftPanel
        ) : (
          <List disablePadding sx={{ flex: 1, overflowY: "auto" }}>
            {listLoading && threads.length === 0 && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  padding: "32px",
                }}
              >
                <CircularProgress size={24} />
              </Box>
            )}
            {threads.length === 0 && !listLoading && <NoConversations />}
            {threads.map((thread) => (
              <ThreadItem
                key={thread.ref}
                thread={thread}
                selected={thread.ref === selectedRef}
                onClick={() => handleSelectThread(thread.ref)}
              />
            ))}
          </List>
        )}
      </Box>

      {/* Right panel */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderTop: "1px solid #EAECF0",
          background: "white",
        }}
      >
        {view === "sms" ? (
          selectedRef ? (
            smsConversationPanel
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#FAFAFA",
              }}
            >
              <Typography variant="bodySmall" color="text.secondary">
                Select a conversation
              </Typography>
            </Box>
          )
        ) : view === "email" ? (
          emailDetailPanel
        ) : showInAppConversation ? (
          inAppConversationPanel
        ) : isDeactivated ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#FAFAFA",
            }}
          >
            <Typography variant="bodySmall" color="text.secondary">
              Select a conversation
            </Typography>
          </Box>
        ) : (
          <EmptyState onCreate={handleNewConversation} />
        )}
      </Box>
      <AttachmentDialog
        attachment={openAttachment}
        onClose={() => setOpenAttachment(null)}
      />
    </Box>
  );
};
